import mermaid from 'mermaid';
import elkLayouts from '@mermaid-js/layout-elk';

function resolveMermaidApi() {
  const candidates = [
    mermaid,
    mermaid?.default,
    mermaid?.mermaid,
    globalThis?.mermaid,
    globalThis?.mermaid?.default,
    globalThis?.mermaid?.mermaid
  ];

  return candidates.find((candidate) =>
    candidate &&
    typeof candidate.initialize === 'function' &&
    typeof candidate.render === 'function'
  ) || null;
}

let mermaidApi = resolveMermaidApi();
let initialized = false;
let initializedTheme = null;
let layoutLoadersRegistered = false;
const registeredContainers = new Set();
let themeObserver = null;
let themeUpdateQueued = false;
const diagramStates = new WeakMap();
const ZOOM_STEP = 0.2;
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 4;
// Desktop-like layout width for diagrams that size from the viewport.
// Gantt: useWidth. C4: screen.availWidth (overridden during render).
const DIAGRAM_LAYOUT_WIDTH = 1200;

function isDarkMode() {
  return document.body.classList.contains('dark-portal');
}

function getThemeName() {
  return isDarkMode() ? 'dark' : 'default';
}

function registerLayoutLoaders() {
  if (layoutLoadersRegistered) return;
  if (typeof mermaidApi?.registerLayoutLoaders !== 'function') return;

  mermaidApi.registerLayoutLoaders(elkLayouts);
  layoutLoadersRegistered = true;
}

function findMermaidDirectiveEnd(source, startIdx) {
  let depth = 1;
  let inString = null;
  let escaped = false;
  let i = startIdx;

  while (i < source.length) {
    const char = source[i];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === inString) {
        inString = null;
      }
      i += 1;
      continue;
    }

    if (char === '"' || char === "'") {
      inString = char;
      i += 1;
      continue;
    }

    if (char === "{") {
      depth += 1;
      i += 1;
      continue;
    }

    if (char === "}") {
      depth -= 1;
      if (depth === 0 && source.slice(i + 1, i + 3) === "%%") {
        return i;
      }
      i += 1;
      continue;
    }

    i += 1;
  }

  return null;
}

function stripMermaidDirectives(source) {
  let output = "";
  let i = 0;

  while (i < source.length) {
    const startIdx = source.indexOf("%%{", i);

    if (startIdx === -1) {
      output += source.slice(i);
      break;
    }

    output += source.slice(i, startIdx);
    const closingIdx = findMermaidDirectiveEnd(source, startIdx + 3);

    if (closingIdx === null) {
      output += source.slice(startIdx);
      break;
    }

    i = closingIdx + 3;
  }

  return output;
}

export function sanitizeSource(raw) {
  return stripMermaidDirectives(raw)
    .replace(/^\s*<pre[^>]*>/i, '')
    .replace(/<\/pre>\s*$/i, '')
    .trim();
}

export function initMermaid() {
  const theme = getThemeName();
  if (initialized && initializedTheme === theme) return;

  mermaidApi = mermaidApi || resolveMermaidApi();
  if (!mermaidApi) {
    throw new Error('Mermaid API initialize() is unavailable');
  }

  registerLayoutLoaders();
  // useMaxWidth:true sets SVG width="100%", which scales the viewBox down to
  // the container (squished diagrams on mobile). Keep pixel size; pan/zoom
  // handles overflow.
  //
  // Gantt lays out from parentElement.offsetWidth unless useWidth is set.
  // C4 lays out from screen.availWidth — see withStableDiagramLayoutWidth().
  const nativeSize = { useMaxWidth: false };

  mermaidApi.initialize({
    startOnLoad: false,
    securityLevel: 'strict',
    maxTextSize: 50000,
    suppressErrorRendering: false,
    htmlLabels: true,
    theme: theme,
    fontFamily: 'inherit',
    flowchart: nativeSize,
    sequence: nativeSize,
    gantt: { useMaxWidth: false, useWidth: DIAGRAM_LAYOUT_WIDTH },
    journey: nativeSize,
    timeline: nativeSize,
    class: nativeSize,
    state: nativeSize,
    er: nativeSize,
    pie: nativeSize,
    quadrantChart: nativeSize,
    xyChart: nativeSize,
    requirement: nativeSize,
    mindmap: nativeSize,
    ishikawa: nativeSize,
    kanban: nativeSize,
    gitGraph: nativeSize,
    c4: nativeSize,
    sankey: nativeSize,
    block: nativeSize,
    packet: nativeSize,
    treeView: nativeSize,
    architecture: nativeSize,
    eventmodeling: nativeSize,
    radar: nativeSize,
    venn: nativeSize,
    treemap: nativeSize,
  });

  initialized = true;
  initializedTheme = theme;
}

async function withStableDiagramLayoutWidth(callback) {
  const screenObj = globalThis.screen;
  if (!screenObj || !(screenObj.availWidth < DIAGRAM_LAYOUT_WIDTH)) {
    return callback();
  }

  const previousDescriptor = Object.getOwnPropertyDescriptor(screenObj, 'availWidth');
  let overridden = false;

  try {
    Object.defineProperty(screenObj, 'availWidth', {
      configurable: true,
      enumerable: true,
      get: () => DIAGRAM_LAYOUT_WIDTH,
    });
    overridden = true;
  } catch {
    return callback();
  }

  try {
    return await callback();
  } finally {
    if (!overridden) return;

    try {
      if (previousDescriptor) {
        Object.defineProperty(screenObj, 'availWidth', previousDescriptor);
      } else {
        delete screenObj.availWidth;
      }
    } catch {
      // Ignore restore failures; next render can try again.
    }
  }
}

async function renderMermaidSvg(id, source) {
  mermaidApi = mermaidApi || resolveMermaidApi();
  if (!mermaidApi || typeof mermaidApi.render !== 'function') {
    throw new Error('Mermaid API render() is unavailable');
  }

  return withStableDiagramLayoutWidth(() => mermaidApi.render(id, source));
}

export function getMermaidApi() {
  mermaidApi = mermaidApi || resolveMermaidApi();
  if (!mermaidApi) return null;

  // Wrap render so callers (e.g. TinyMCE) get the C4 layout-width fix
  // without needing their own changes.
  return {
    initialize: (...args) => mermaidApi.initialize(...args),
    render: (id, source, ...rest) =>
      withStableDiagramLayoutWidth(() => mermaidApi.render(id, source, ...rest)),
  };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getDiagramState(wrapper) {
  let state = diagramStates.get(wrapper);
  if (!state) {
    state = {
      scale: 1,
      x: 0,
      y: 0,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      startX: 0,
      startY: 0,
      pinching: false,
      pinchStartDistance: 1,
      pinchStartScale: 1,
      pinchStartX: 0,
      pinchStartY: 0,
      pinchStartMidX: 0,
      pinchStartMidY: 0,
    };
    diagramStates.set(wrapper, state);
  }

  return state;
}

function pointerDistance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y) || 1;
}

function beginPinch(state, viewport, pointers) {
  const [first, second] = pointers.values();
  const rect = viewport.getBoundingClientRect();

  state.pinching = true;
  state.dragging = false;
  state.pinchStartDistance = pointerDistance(first, second);
  state.pinchStartScale = state.scale;
  state.pinchStartX = state.x;
  state.pinchStartY = state.y;
  state.pinchStartMidX = (first.x + second.x) / 2 - rect.left;
  state.pinchStartMidY = (first.y + second.y) / 2 - rect.top;
}

function applyPinch(state, viewport, pointers) {
  const [first, second] = pointers.values();
  const rect = viewport.getBoundingClientRect();
  const distance = pointerDistance(first, second);
  const scale = clamp(
    state.pinchStartScale * (distance / state.pinchStartDistance),
    MIN_ZOOM,
    MAX_ZOOM,
  );
  const midX = (first.x + second.x) / 2 - rect.left;
  const midY = (first.y + second.y) / 2 - rect.top;
  const contentX = (state.pinchStartMidX - state.pinchStartX) / state.pinchStartScale;
  const contentY = (state.pinchStartMidY - state.pinchStartY) / state.pinchStartScale;

  state.scale = scale;
  state.x = midX - contentX * scale;
  state.y = midY - contentY * scale;
}

function applyDiagramTransform(wrapper) {
  const state = getDiagramState(wrapper);
  const canvas = wrapper.querySelector('.mermaid-diagram__canvas');
  const resetButton = wrapper.querySelector('[data-mermaid-control="reset"]');
  if (!canvas) return;

  canvas.style.transform = `translate(${state.x}px, ${state.y}px) scale(${state.scale})`;
  if (resetButton) resetButton.textContent = `${Math.round(state.scale * 100)}%`;
}

function setDiagramZoom(wrapper, nextScale) {
  const state = getDiagramState(wrapper);
  state.scale = clamp(nextScale, MIN_ZOOM, MAX_ZOOM);
  applyDiagramTransform(wrapper);
}

function zoomDiagramAtPoint(wrapper, viewport, nextScale, clientX, clientY) {
  const state = getDiagramState(wrapper);
  const rect = viewport.getBoundingClientRect();
  const scale = clamp(nextScale, MIN_ZOOM, MAX_ZOOM);
  const pointX = clientX - rect.left;
  const pointY = clientY - rect.top;
  const contentX = (pointX - state.x) / state.scale;
  const contentY = (pointY - state.y) / state.scale;

  state.scale = scale;
  state.x = pointX - contentX * scale;
  state.y = pointY - contentY * scale;
  applyDiagramTransform(wrapper);
}

function resetDiagramView(wrapper) {
  const state = getDiagramState(wrapper);
  state.scale = 1;
  state.x = 0;
  state.y = 0;
  applyDiagramTransform(wrapper);
}

function toggleDiagramFullscreen(wrapper) {
  if (document.fullscreenElement === wrapper) {
    document.exitFullscreen?.();
    return;
  }

  if (wrapper.requestFullscreen) {
    wrapper.requestFullscreen();
  } else {
    wrapper.classList.toggle('mermaid-diagram--fullscreen-fallback');
  }
}

function isDiagramFullscreen(wrapper) {
  return document.fullscreenElement === wrapper ||
    wrapper.classList.contains('mermaid-diagram--fullscreen-fallback');
}

function createDiagramToolbar(wrapper) {
  const toolbar = document.createElement('div');
  toolbar.className = 'mermaid-diagram__toolbar';
  toolbar.setAttribute('aria-label', 'Mermaid diagram controls');

  [
    { action: 'zoom-out', label: 'Zoom out', icon: 'far fa-minus' },
    { action: 'reset', label: 'Reset zoom', text: '100%' },
    { action: 'zoom-in', label: 'Zoom in', icon: 'far fa-plus' },
    { action: 'fullscreen', label: 'Fullscreen', icon: 'far fa-expand' },
  ].forEach((control) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'mermaid-diagram__control';
    button.dataset.mermaidControl = control.action;
    button.setAttribute('aria-label', control.label);
    button.setAttribute('title', control.label);
    button.setAttribute('data-tippy-content', control.label);

    if (control.icon) {
      const icon = document.createElement('i');
      icon.className = control.icon;
      icon.setAttribute('aria-hidden', 'true');
      button.appendChild(icon);
    } else {
      button.textContent = control.text;
    }

    toolbar.appendChild(button);
  });

  toolbar.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mermaid-control]');
    if (!button) return;

    const state = getDiagramState(wrapper);
    switch (button.dataset.mermaidControl) {
      case 'zoom-out':
        setDiagramZoom(wrapper, state.scale - ZOOM_STEP);
        break;
      case 'zoom-in':
        setDiagramZoom(wrapper, state.scale + ZOOM_STEP);
        break;
      case 'reset':
        resetDiagramView(wrapper);
        break;
      case 'fullscreen':
        toggleDiagramFullscreen(wrapper);
        break;
    }
  });

  return toolbar;
}

function enableDiagramPan(wrapper, viewport) {
  const pointers = new Map();

  const clearDragUi = () => {
    viewport.classList.remove('mermaid-diagram__viewport--dragging');
  };

  const beginDrag = (state, clientX, clientY) => {
    state.pinching = false;
    state.dragging = true;
    state.dragStartX = clientX;
    state.dragStartY = clientY;
    state.startX = state.x;
    state.startY = state.y;
    viewport.classList.add('mermaid-diagram__viewport--dragging');
  };

  viewport.addEventListener('pointerdown', (event) => {
    if (event.button !== 0) return;

    const state = getDiagramState(wrapper);
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    viewport.setPointerCapture?.(event.pointerId);

    if (pointers.size >= 2) {
      clearDragUi();
      beginPinch(state, viewport, pointers);
      return;
    }

    beginDrag(state, event.clientX, event.clientY);
  });

  viewport.addEventListener('pointermove', (event) => {
    if (!pointers.has(event.pointerId)) return;

    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const state = getDiagramState(wrapper);

    if (state.pinching && pointers.size >= 2) {
      event.preventDefault();
      applyPinch(state, viewport, pointers);
      applyDiagramTransform(wrapper);
      return;
    }

    if (!state.dragging) return;

    state.x = state.startX + event.clientX - state.dragStartX;
    state.y = state.startY + event.clientY - state.dragStartY;
    applyDiagramTransform(wrapper);
  });

  const endPointer = (event) => {
    if (!pointers.has(event.pointerId)) return;

    pointers.delete(event.pointerId);
    try {
      viewport.releasePointerCapture?.(event.pointerId);
    } catch {
      // Pointer may already be released.
    }

    const state = getDiagramState(wrapper);

    if (pointers.size >= 2) {
      beginPinch(state, viewport, pointers);
      return;
    }

    state.pinching = false;

    if (pointers.size === 1) {
      const remaining = pointers.values().next().value;
      beginDrag(state, remaining.x, remaining.y);
      return;
    }

    state.dragging = false;
    clearDragUi();
  };

  viewport.addEventListener('pointerup', endPointer);
  viewport.addEventListener('pointercancel', endPointer);
  viewport.addEventListener('lostpointercapture', endPointer);
  
  viewport.addEventListener('wheel', (event) => {
    if (!isDiagramFullscreen(wrapper)) return;

    event.preventDefault();

    const state = getDiagramState(wrapper);
    const direction = event.deltaY > 0 ? -1 : 1;
    zoomDiagramAtPoint(
      wrapper,
      viewport,
      state.scale + direction * ZOOM_STEP,
      event.clientX,
      event.clientY,
    );
  }, { passive: false });
}

function renderSvgIntoWrapper(wrapper, svg) {
  wrapper.classList.remove('mermaid-diagram--error');
  wrapper.innerHTML = '';

  const toolbar = createDiagramToolbar(wrapper);
  const viewport = document.createElement('div');
  viewport.className = 'mermaid-diagram__viewport';
  viewport.setAttribute('tabindex', '0');
  viewport.setAttribute('aria-label', 'Mermaid diagram. Drag to pan, pinch to zoom, or use the controls to zoom or open fullscreen.');

  const canvas = document.createElement('div');
  canvas.className = 'mermaid-diagram__canvas';
  canvas.innerHTML = svg;

  viewport.appendChild(canvas);
  wrapper.appendChild(toolbar);
  wrapper.appendChild(viewport);

  globalThis.initializeTippy?.(toolbar);
  enableDiagramPan(wrapper, viewport);
  applyDiagramTransform(wrapper);
}

export async function renderMermaidBlocks(container) {
  const preBlocks = Array.from(container.querySelectorAll('pre.language-mermaid, pre.mermaid'));
  const codeBlocks = Array.from(container.querySelectorAll('pre > code.language-mermaid, pre > code.mermaid'));

  codeBlocks.forEach((codeEl) => {
    const preEl = codeEl.parentElement;
    if (preEl && !preBlocks.includes(preEl)) preBlocks.push(preEl);
  });

  if (preBlocks.length === 0) return;

  initMermaid();

  for (const preEl of preBlocks) {
    if (!preEl) continue;

      const codeEl = preEl.querySelector('code.language-mermaid, code.mermaid');
      const source = sanitizeSource((codeEl || preEl).textContent || '');
      if (!source) continue;

      const wrapper = document.createElement('div');
      wrapper.className = 'mermaid-diagram'; 
      wrapper.setAttribute('contenteditable', 'false');
      wrapper.dataset.mermaidSource = source;

      const id = 'mermaid-' + Math.random().toString(36).slice(2, 10);

    try {
      const { svg } = await renderMermaidSvg(id, source);
      renderSvgIntoWrapper(wrapper, svg);
    } catch (err) {
      wrapper.classList.add('mermaid-diagram--error');
      wrapper.textContent = 'Diagram error: ' + (err.message || 'Invalid syntax');

      const errorEl = document.getElementById('d' + id);
      if (errorEl) errorEl.remove();
    }

    preEl.replaceWith(wrapper);
  }
}

async function rerenderExistingDiagrams(container) {
  const wrappers = Array.from(container.querySelectorAll('.mermaid-diagram[data-mermaid-source]'));
  if (wrappers.length === 0) return;

  initMermaid();

  for (const wrapper of wrappers) {
    const source = sanitizeSource(wrapper.dataset.mermaidSource || '');
    if (!source) continue;

    const id = 'mermaid-' + Math.random().toString(36).slice(2, 10);

    try {
      const { svg } = await renderMermaidSvg(id, source);
      renderSvgIntoWrapper(wrapper, svg);
    } catch (err) {
      wrapper.classList.add('mermaid-diagram--error');
      wrapper.textContent = 'Diagram error: ' + (err.message || 'Invalid syntax');

      const errorEl = document.getElementById('d' + id);
      if (errorEl) errorEl.remove();
    }
  }
}

async function rerenderRegisteredContainers() {
  for (const container of Array.from(registeredContainers)) {
    if (!container || !container.isConnected) {
      registeredContainers.delete(container);
      continue;
    }

    await rerenderExistingDiagrams(container);
  }
}

function queueThemeUpdate() {
  if (themeUpdateQueued) return;
  themeUpdateQueued = true;

  requestAnimationFrame(async () => {
    themeUpdateQueued = false;

    const theme = getThemeName();
    if (initializedTheme === theme) return;

    initialized = false;
    initMermaid();
    await rerenderRegisteredContainers();
  });
}

function ensureThemeObserver() {
  if (themeObserver) return;

  themeObserver = new MutationObserver(() => {
    queueThemeUpdate();
  });

  themeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
}

export function registerMermaidContainer(container) {
  if (!container) return;
  registeredContainers.add(container);
  ensureThemeObserver();
}

export function unregisterMermaidContainer(container) {
  if (!container) return;
  registeredContainers.delete(container);
}
